import Sidebar from "../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { getCuotaPorUnidad, insertCuota, updateCuota } from "../../../api/cuotaAdminService";
import Swal from "sweetalert2";

export default function AdminConfigPage() {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";
  const [isAdminConfigured, setIsAdminConfigured] = useState(false);
  const [adminData, setAdminData] = useState({
    unidad_recidencial_codigo: "",
    monthlyFee: "",
    paymentDay: "",
    bank: "",
    paymentLink: "",
    codigo: "", // Para almacenar el cau_codigo si existe
  });

  // Cargar datos iniciales al montar el componente
  useEffect(() => {
    const fetchCuota = async () => {
      try {
        const unidadCodigo = unidadId; // Este valor viene de session
        const response = await getCuotaPorUnidad({ unidad_recidencial_codigo: unidadCodigo });
        if (Array.isArray(response) && response.length > 0 && response[0].cau_codigo !== 0) {
          const cuota = response[0];
          setAdminData({
            unidad_recidencial_codigo: cuota.cau_unidad_residencial,
            monthlyFee: cuota.cau_valor.toString(),
            paymentDay: cuota.cau_dia_pago.toString(),
            bank: cuota.cau_banco,
            paymentLink: cuota.cau_link_pago,
            codigo: cuota.cau_codigo.toString(),
          });
          setIsAdminConfigured(true);
        } else {
          // Si no hay datos válidos, establecer solo el código de unidad y mantener los demás campos vacíos
          setAdminData((prev) => ({
            ...prev,
            unidad_recidencial_codigo: unidadCodigo,
          }));
          setIsAdminConfigured(false);
        }
      } catch (error) {
        console.error("Error al cargar la cuota:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error al cargar la cuota",
          confirmButtonColor: "#d33",
        });
      }
    };
    if (unidadId) {
      fetchCuota();
    }
  }, [unidadId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAdminData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      // Validar que los campos requeridos no estén vacíos
      if (!adminData.monthlyFee || !adminData.paymentDay || !adminData.bank || !adminData.paymentLink) {
        Swal.fire({
          icon: "warning",
          title: "Advertencia",
          text: "Por favor, complete todos los campos",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      if (isAdminConfigured) {
        // Modificar cuota existente
        const payload = {
          codigo: adminData.codigo,
          unidad_recidencial_codigo: adminData.unidad_recidencial_codigo,
          valor: parseFloat(adminData.monthlyFee),
          link_Pago: adminData.paymentLink,
          Banco: adminData.bank,
          dia_pago: parseInt(adminData.paymentDay),
        };
        await updateCuota(payload);
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Cuota actualizada exitosamente",
          confirmButtonColor: "#3085d6",
        });
      } else {
        // Insertar nueva cuota
        const payload = {
          unidad_recidencial_codigo: adminData.unidad_recidencial_codigo,
          valor: parseFloat(adminData.monthlyFee),
          link_Pago: adminData.paymentLink,
          Banco: adminData.bank,
          cau_dia_pago: parseInt(adminData.paymentDay),
        };
        await insertCuota(payload);
        setIsAdminConfigured(true);
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Cuota guardada exitosamente",
          confirmButtonColor: "#3085d6",
        });
      }
    } catch (error) {
      console.error("Error al guardar la cuota:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al guardar la cuota",
        confirmButtonColor: "#d33",
      });
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      {/* Sidebar */}
      <Sidebar />

      {/* Contenido principal */}
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-military-600">Configuración de Administración</h2>

        <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white">
          <CardHeader>
            <CardTitle className="text-xl text-military-600 flex items-center gap-2">
              {isAdminConfigured ? "Actualizar Configuración" : "Configurar Administración"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <label htmlFor="monthlyFee" className="block text-sm font-medium text-gray-700 mb-1">
                  Valor de la Cuota Mensual
                </label>
                <Input
                  id="monthlyFee"
                  name="monthlyFee"
                  type="number"
                  value={adminData.monthlyFee}
                  onChange={handleInputChange}
                  placeholder="Valor de la cuota mensual"
                  className="w-full bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
                />
              </div>
              <div>
                <label htmlFor="paymentDay" className="block text-sm font-medium text-gray-700 mb-1">
                  Día de Pago
                </label>
                <Input
                  id="paymentDay"
                  name="paymentDay"
                  type="number"
                  value={adminData.paymentDay}
                  onChange={handleInputChange}
                  placeholder="Día de pago"
                  className="w-full bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
                />
              </div>
              <div>
                <label htmlFor="bank" className="block text-sm font-medium text-gray-700 mb-1">
                  Banco
                </label>
                <Input
                  id="bank"
                  name="bank"
                  value={adminData.bank}
                  onChange={handleInputChange}
                  placeholder="Banco"
                  className="w-full bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
                />
              </div>
              <div>
                <label htmlFor="paymentLink" className="block text-sm font-medium text-gray-700 mb-1">
                  Link de Pagos
                </label>
                <Input
                  id="paymentLink"
                  name="paymentLink"
                  type="url"
                  value={adminData.paymentLink}
                  onChange={handleInputChange}
                  placeholder="Link de pagos"
                  className="w-full bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
                />
              </div>
              <Button
                onClick={handleSave}
                className="w-full bg-military-500 hover:bg-military-600 text-white rounded-lg transition"
              >
                {isAdminConfigured ? "Actualizar" : "Insertar"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}